import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Paper, Typography, TextField, Button, Alert, CircularProgress, InputAdornment, IconButton } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { API_BASE_URL } from './config';

const ResetPasswordPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [token, setToken] = useState('');

    useEffect(() => {
        // Extract token from URL
        const params = new URLSearchParams(location.search);
        const tokenParam = params.get('token');
        if (tokenParam) {
            setToken(tokenParam);
        } else {
            setError(t('invalid_reset_link'));
        }
    }, [location, t]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (password.length < 6) {
            setError(t('password_too_short'));
            return;
        }

        if (password !== confirmPassword) {
            setError(t('passwords_do_not_match'));
            return;
        }

        setIsLoading(true);

        try {
            const response = await fetch(`${API_BASE_URL}/reset_password.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, password })
            });

            const data = await response.json();

            if (data.success) {
                setSuccess(true);
            } else {
                setError(data.message || t('reset_password_failed'));
            }
        } catch (err) {
            setError(t('network_error'));
        } finally {
            setIsLoading(false);
        }
    };

    if (success) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: 4,
                    height: '100vh',
                    boxSizing: 'border-box',
                }}
            >
                <Paper
                    elevation={6}
                    sx={{
                        padding: { xs: 4, md: 6 },
                        borderRadius: 2,
                        textAlign: 'center',
                        maxWidth: 450,
                        width: '100%',
                    }}
                >
                    <Box sx={{ 
                        width: 80, 
                        height: 80, 
                        borderRadius: '50%', 
                        bgcolor: '#e8f5e9', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        margin: '0 auto 20px'
                    }}>
                        <CheckCircleIcon sx={{ fontSize: 40, color: '#4caf50' }} />
                    </Box>
                    <Typography variant="h5" component="h2" gutterBottom>
                        {t('password_reset_success')}
                    </Typography>
                    <Typography variant="body1" paragraph color="text.secondary">
                        {t('password_reset_success_message')}
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={() => navigate('/')}
                        sx={{ mt: 2 }}
                    >
                        {t('go_to_login')}
                    </Button>
                </Paper>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: 4,
                height: '100vh',
                boxSizing: 'border-box',
            }}
        >
            <Paper
                elevation={6}
                sx={{
                    padding: { xs: 4, md: 6 },
                    borderRadius: 2,
                    textAlign: 'center',
                    maxWidth: 450,
                    width: '100%',
                }}
            >
                <Box sx={{ 
                    width: 80, 
                    height: 80, 
                    borderRadius: '50%', 
                    bgcolor: '#e3f2fd', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    margin: '0 auto 20px'
                }}>
                    <LockOutlinedIcon sx={{ fontSize: 40, color: '#1976d2' }} />
                </Box>
                <Typography variant="h5" component="h2" gutterBottom>
                    {t('reset_password_title')}
                </Typography>
                <Typography variant="body1" paragraph color="text.secondary">
                    {t('reset_password_prompt')}
                </Typography>

                <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                    <TextField
                        fullWidth
                        label={t('new_password')}
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        margin="normal"
                        required
                        disabled={isLoading}
                        helperText={t('password_helper')}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <LockOutlinedIcon color="action" />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowPassword(!showPassword)}
                                        edge="end"
                                        size="small"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    
                    <TextField
                        fullWidth
                        label={t('confirm_password')}
                        type={showPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        margin="normal"
                        required
                        disabled={isLoading}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <LockOutlinedIcon color="action" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    
                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        disabled={isLoading || !token}
                        sx={{
                            mt: 3,
                            mb: 2,
                            padding: '12px 30px',
                            borderRadius: '8px',
                            fontSize: '1rem',
                            fontWeight: 'bold',
                        }}
                    >
                        {isLoading ? <CircularProgress size={24} /> : t('reset_password_btn')}
                    </Button>
                    
                    <Button
                        variant="text"
                        onClick={() => navigate('/')}
                        sx={{ mt: 1 }}
                    >
                        {t('back_to_login')}
                    </Button>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}
            </Paper>
        </Box>
    );
};

export default ResetPasswordPage;
